import { useWebSocket } from "@vueuse/core";
import { reactive } from "vue";
import { useToast } from "vue-toast-notification";
import { USER_NAME_KEY, WS_INDICATION_KEY } from "@/helpers/localStorageLabels";
import { getAccessToken, refreshAccessToken } from "@/services/authService";
import store from "@/store";
import { AUTH_LOGOUT } from "@/store/actions/auth";
import { REPORTS_INDICATION } from "@/store/actions/users";

// Shared reactive state — main.js exposes this as $batchProgress so components
// always read from the same object regardless of when the WebSocket was
// (re)initialized. Reassigning the handler at auth transitions would otherwise
// leave components bound to a stale progress proxy.
export const batchProgress = reactive({});

// Reconnect budget: 1s, 2s, 4s, 8s, 16s, 32s, 32s, 32s ~ 127s. Backoff
// rather than a flat delay because a short budget force-logs-out every
// analyst on every websocket-service deploy.
//
// Bounded by websocket-service's CACHE_SESSION_GRACE_SECONDS (180s), NOT
// its CACHE_OFFLINE_GRACE_SECONDS (30s) — the client must give up before
// the server tears the session down, but outlasting the presence window
// is fine since a reconnect just re-adds them to the round-robin.
export const RECONNECT_MAX_ATTEMPTS = 8;

// A connection has to last this long before it counts as healthy enough to
// refund the budget. Without it, a server that accepts the handshake and then
// rejects the auth frame refunds on every cycle and the budget never runs out.
export const STABLE_CONNECTION_MS = 10000;

// Consecutive auth rejections (close 1008) before giving up. Separate from the
// reconnect budget because those closes follow a *successful* handshake, so
// they would otherwise look like healthy connections.
export const MAX_AUTH_REJECTIONS = 3;
export const RECONNECT_BASE_DELAY_MS = 1000;
export const RECONNECT_MAX_DELAY_MS = 32000;

/** Delay before attempt N (1-based). */
export const reconnectDelayFor = (attempt) =>
	Math.min(
		RECONNECT_BASE_DELAY_MS * 2 ** Math.max(0, attempt - 1),
		RECONNECT_MAX_DELAY_MS,
	);

/** Total delay across the whole budget, for the pairing assertion above. */
export const totalReconnectDelayMs = () => {
	let total = 0;
	for (let i = 1; i <= RECONNECT_MAX_ATTEMPTS; i++)
		total += reconnectDelayFor(i);
	return total;
};

// Server close code for a rejected JWT (websocket-service closes with 1008
// when verifyToken fails). Retrying with the same expired token just loops, so
// this triggers a refresh before the next attempt.
const WS_CLOSE_INVALID_TOKEN = 1008;

export const useWebSocketHandler = () => {
	const $toast = useToast();
	const progress = batchProgress;

	// Set when the socket is closed with 1008 so the next onConnected refreshes
	// the access token before authenticating again.
	let needsTokenRefresh = false;
	// Guards against AUTH_LOGOUT being dispatched more than once.
	let gaveUp = false;
	// Set when the *server* logged us out, so the reconnect machinery stands
	// down instead of racing the page reload.
	let serverLoggedOut = false;
	// Reconnect attempt counter, refunded only by a connection that lasted.
	let attempt = 0;
	// Consecutive 1008 closes.
	let authRejections = 0;
	// When the current socket opened, 0 while closed.
	let connectedAt = 0;

	// The analyst is not in the round-robin here, so leaving them on a
	// half-working page lets wires pile up unassigned.
	const giveUp = (reason) => {
		if (gaveUp || serverLoggedOut) return;
		gaveUp = true;
		localStorage.setItem(WS_INDICATION_KEY, false);
		console.warn(`WebSocket unrecoverable (${reason}) — logging out`);
		$toast.error("Connection to the server was lost. Please sign in again.");
		// AUTH_LOGOUT ignores its payload, so the reason rides the toast above.
		store.dispatch(AUTH_LOGOUT, { token: "", isForce: false });
	};

	const hostForWEbsocket = window.location.host.includes("localhost")
		? "ws://localhost:4567/api/v1/ws/"
		: `wss://${window.location.host}/api/v1/ws/`;

	// console.log(`Connecting to WebSocket at ${hostForWEbsocket}`);

	// Runs on every connect, so the server re-registers the analyst in the
	// round-robin as soon as the socket is back.
	const authenticate = async () => {
		if (needsTokenRefresh) {
			needsTokenRefresh = false;
			try {
				await refreshAccessToken();
			} catch {
				// Returning here would strand an OPEN but unauthenticated socket: no
				// further close event fires, so onFailed never runs.
				giveUp("token refresh failed");
				return;
			}
		}

		const token = getAccessToken();
		const username = localStorage.getItem(USER_NAME_KEY);
		if (!token) {
			giveUp("no access token");
			return;
		}

		localStorage.setItem(WS_INDICATION_KEY, true);
		// Use Bearer token format for WebSocket auth
		send(
			JSON.stringify({
				type: "auth",
				token: `Bearer ${token}`,
				username: username,
			}),
		);
	};

	const { send, status, open, close } = useWebSocket(hostForWEbsocket, {
		// vueuse re-reads this object on every close, so `delay` can be an
		// accessor — the v10 API has no backoff option.
		autoReconnect: {
			get delay() {
				attempt += 1;
				return reconnectDelayFor(attempt);
			},
			// `attempt` was already bumped by the delay getter above, so compare
			// inclusively or the last attempt's delay is computed and never used
			// — 7 reconnects instead of the 8 the budget documents.
			retries: () =>
				!gaveUp && !serverLoggedOut && attempt <= RECONNECT_MAX_ATTEMPTS,
			onFailed: () => giveUp("reconnect budget exhausted"),
		},
		onConnected: () => {
			console.log("WebSocket connected");
			// Deliberately not refunding the budget here: the handshake opening
			// says nothing about whether the server will accept our auth frame,
			// and a server rejecting every frame with 1008 opens the socket every
			// time. onDisconnected refunds instead, once a connection has proved
			// it lasted.
			connectedAt = Date.now();
			authenticate();
		},
		onDisconnected: (_wsInstance, event) => {
			localStorage.setItem(WS_INDICATION_KEY, false);

			// vueuse calls this before it reads the autoReconnect options, so a
			// refund here is seen by this close's own retry decision.
			if (connectedAt && Date.now() - connectedAt >= STABLE_CONNECTION_MS) {
				attempt = 0;
				authRejections = 0;
			}
			connectedAt = 0;

			if (event?.code === WS_CLOSE_INVALID_TOKEN) {
				// Retrying the same rejected token burns the budget against Keycloak's
				// rate-limited JWKS endpoint.
				needsTokenRefresh = true;
				authRejections += 1;
				if (authRejections >= MAX_AUTH_REJECTIONS) {
					// The handshake keeps succeeding, so the reconnect budget alone
					// would never run out and the tab would spin on /refresh forever.
					giveUp("repeated authentication rejection");
				}
			}
			console.log("WebSocket disconnected");
		},
		onError: (_wsInstance, event) => {
			console.error("WebSocket error:", {
				message: event.message || "Unknown error",
				type: event.type,
				target: event.target,
				timeStamp: event.timeStamp,
			});
			localStorage.setItem(WS_INDICATION_KEY, false);
		},
		onMessage: (_, event) => {
			const messageData = event.data;

			if (messageData.includes("logout")) {
				const { message } = JSON.parse(messageData);
				// A reconnect ~1s later would beat the 2.5s reload and re-register an
				// analyst just hard-logged-out: the WS handshake checks the JWT
				// signature and expiry, not its revocation.
				serverLoggedOut = true;
				close();

				$toast.error(message || "You've been logged out");

				setTimeout(() => {
					$toast.clear();
					window.location.reload();
				}, 2500);
				return;
			} else if (messageData.includes("Update on batch progress")) {
				const parsedMessageData = JSON.parse(messageData);
				const { batchId, progress: batchProgress } = parsedMessageData;

				if (batchId && batchProgress !== undefined) {
					// making sure we'll update the progress only to go up
					if (!progress[batchId] || batchProgress > progress[batchId]) {
						progress[batchId] = batchProgress; // Update the reactive object
					}
				}
			} else if (messageData.includes("report-is-ready")) {
				const parsedMessageData = JSON.parse(messageData);
				const reportType = parsedMessageData.type.split("_")[0];
				store.dispatch(REPORTS_INDICATION, {
					type: reportType,
					action: "remove",
				});
				$toast.success("Report is ready!", {
					position: "top-right",
				});
			} else if (messageData.includes("provider-error")) {
				try {
					const parsedData = JSON.parse(messageData);
					if (parsedData.type === "provider-error") {
						$toast.error(
							`Provider error: ${parsedData.providerMessage || "Unknown error"} (${parsedData.source || "unknown"}: ${parsedData.providerCode || "?"})`,
							{
								position: "bottom",
								duration: 10000,
							},
						);
					}
				} catch {
					// Ignore malformed provider-error messages
				}
			} else if (messageData.includes("ach-line-unlock")) {
				// Check unlock BEFORE lock — "ach-line-unlock" contains "ach-line-lock"
				try {
					const parsedData = JSON.parse(messageData);
					if (parsedData.type === "ach-line-unlock") {
						store.commit("supervision/ACH_LINE_UNLOCK", {
							querylogId: parsedData.querylogId,
							fieldIndex: parsedData.fieldIndex,
						});
					}
				} catch {
					// Ignore malformed ach-line-unlock messages
				}
			} else if (messageData.includes("ach-line-lock")) {
				try {
					const parsedData = JSON.parse(messageData);
					if (parsedData.type === "ach-line-lock") {
						store.commit("supervision/ACH_LINE_LOCK", {
							querylogId: parsedData.querylogId,
							fieldIndex: parsedData.fieldIndex,
							lockedBy: parsedData.lockedBy,
							lockedByName: parsedData.lockedByName,
						});
					}
				} catch {
					// Ignore malformed ach-line-lock messages
				}
			} else if (messageData.includes("ach-file-team-assigned")) {
				// Team assignment changed — intentionally no UI update here.
				// The querylog list view re-fetches on navigation/filter change,
				// so real-time push of team changes is not needed. If instant
				// updates are required in the future, commit a Vuex mutation
				// to update the file's teamId and assignedWorkers in-place.
			}

			// Send pong response (no token - already authenticated on connect)
			const token = getAccessToken();
			const username = localStorage.getItem(USER_NAME_KEY);
			if (token) {
				localStorage.setItem(WS_INDICATION_KEY, true);
				send(
					JSON.stringify({
						type: "pong",
						username: username,
					}),
				);
			} else {
				return;
			}
		},
	});

	return { progress, status, open, close };
};
